import { SelectChangeEvent, SelectProps, SxProps } from '@mui/material';
import { SelectFieldItem } from '@portals/core/src/components/SelectFieldItem/SelectFieldItem';
import React, { useCallback } from 'react';

import { if6CssPrefix } from '../../../config';
import { SxStyles } from '../../../types';
import { SelectField } from '../SelectField/SelectField';

const styles: SxStyles = {
  select: { flexGrow: 1 },
  item: { p: { lg: 6 } },
};

export type ControlledSelectFieldOptionsProps = {
  value: string | number;
  label: string;
};

export type ControlledSelectFieldProps = Omit<SelectProps, 'onChange' | 'value'> & {
  label?: string;
  items: ControlledSelectFieldOptionsProps[];
  selectedValue?: ControlledSelectFieldOptionsProps['value'];
  onChange: (value: ControlledSelectFieldOptionsProps['value']) => void;
  testId?: string;
  icon?: React.ReactNode | null;
};

export const ControlledSelectField: React.FC<ControlledSelectFieldProps> = ({
  items,
  label = '',
  selectedValue = '',
  onChange,
  testId,
  disabled = false,
  sx,
  icon = null,
  className = 'estate-select',
  ...props
}) => {
  const handleChange = useCallback(
    (e: SelectChangeEvent) => {
      const val = e.target.value;
      onChange(val);
    },
    [onChange]
  );

  return (
    <SelectField
      id={testId}
      data-testid={testId}
      variant="outlined"
      label={label}
      className={`${className} controlled-select`}
      value={selectedValue}
      onChange={handleChange}
      startAdornment={icon}
      disabled={disabled}
      sx={{ ...styles.select, ...sx } as SxProps}
      MenuProps={{
        className: if6CssPrefix,
        classes: {
          paper: `${if6CssPrefix} ${className}-list`,
        },

        MenuListProps: { className: `${className}-menu-list` },
      }}
      {...props}
    >
      {items.map(({ value, label }) => (
        <SelectFieldItem
          id={String(value)}
          key={value}
          value={value}
          checked={value === selectedValue}
          type="radio"
          sx={styles.item}
        >
          {label}
        </SelectFieldItem>
      ))}
    </SelectField>
  );
};

ControlledSelectField.displayName = 'ControlledSelectField';
